import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, useLocation } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { useTranslation } from 'react-i18next';
import ParcelComponent from 'single-spa-react/parcel';
import { mountRootParcel } from 'single-spa';
import Cookies from 'universal-cookie';

import * as http from '_http';
import * as Auth from 'util/auth';
import { hasAppAccess, APP_ROUTES } from 'util/appAccess';
import ErrorBoundary from 'helpers/ErrorBoundary';
import AppBar from 'components/AppBar/AppBar';
import AppLoader from 'components/AppLoader';
import openNotification from 'components/openNotification';
import { LoginTypes } from 'enums/index';
import LoginPage from './pages/LoginPage';
import AccessDeniedPage from './pages/AccessDeniedPage';

const HIDE_INTERCOM_PATHS = [];

function App({ location, changeLanguage }) {
  const [userDetails, setUserDetails] = useState(null);
  const [idpUserDetails, setIdpUserDetails] = useState(null);
  const [specialOrderCustomers, setSpecialOrderCustomers] = useState([]);
  const [selectedSpecialOrderCustomer, setSelectedSpecialOrderCustomer] = useState(null);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState({
    customerId: null,
    opco: null,
    customerDetails: { customerInfo: { name: null } }
  });
  const [isCustomerSelectorVisible, setIsCustomerSelectorVisible] = useState(false);
  const [isCustomerFilterVisible, setIsCustomerFilterVisible] = useState(false);
  const { update: updateIntercom, shutdown: shutdownIntercom } = useIntercom();

  const { i18n, t } = useTranslation();
  const currentLocation = useLocation();

  // set access token as a cookie in local environment
  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'local') {
      const params = new URLSearchParams(location.search);
      const userClaims = params.get('user_claims');

      if (userClaims) {
        const cookies = new Cookies();
        cookies.set('user-claims', userClaims, { path: '/' });
      }
    }
  }, [location.search]);

  // get and set user details
  useEffect(() => {
    const getUserDetails = async () => {
      if (!userDetails) {
        setIsUserLoading(true);
        try {
          const { data: userData } = await http.getRequest('/user');
          if (userData?.userDetails) {
            setUserDetails(userData);
            const { languagePreference } = userData?.userDetails;
            changeLanguage(languagePreference);
          }
        } catch (e) {
          console.log(e);
          openNotification({
            description: t('landing.errorNotification.description.userFetch'),
            className: 'error',
            message: t('landing.errorNotification.message.userFetch')
          });
        }
      }
      setIsUserLoading(false);
    };
    getUserDetails();
  }, [userDetails, setUserDetails]);

  // set default customer as selected customer
  useEffect(() => {
    if (userDetails?.userDetails?.defaultCustomer) {
      const { defaultCustomer } = userDetails.userDetails;
      const defaultCustomerDetails = userDetails.customerList.find(
        customer => customer.customerId === defaultCustomer.customerId && customer.opco === defaultCustomer.opco
      );
      setSelectedCustomer(defaultCustomerDetails);
    }
  }, [userDetails]);

  // initiate intercom instance
  useEffect(() => {
    const { email, languagePreference, onboardedFlag, esyscoUserType, firstName, lastName } =
      userDetails?.userDetails || {};
    const { loginType } = userDetails || {};
    const isOktaLogin = loginType === LoginTypes.OKTA_LOGIN;

    const getIdpUserDetails = async () => {
      try {
        setIsUserLoading(true);
        const { data: oktaUserDetails } = await http.getRequest(`/user/idp`);
        setIdpUserDetails(oktaUserDetails);
      } catch (e) {
        console.error('Failed to update intercom', e);
      }
      setIsUserLoading(false);
    };

    if (isOktaLogin && !idpUserDetails) {
      getIdpUserDetails();
    }

    if (email) {
      if (isOktaLogin && idpUserDetails) {
        const intercomOptions = {
          userId: idpUserDetails?.syscoCustUUID,
          email: email[0],
          languageOverride: languagePreference,
          name: `${idpUserDetails?.firstName ?? ''} ${idpUserDetails?.lastName ?? ''}`.trim(),
          customAttributes: {
            cxFirstName: idpUserDetails?.firstName,
            cxLastName: idpUserDetails?.lastName,
            SyscoPortalCA: onboardedFlag,
            PortalCASubscription: esyscoUserType === 1 ? 'Premium' : 'Standard',
            cxLastSeenPortalCA: new Date().toISOString()
          }
        };
        updateIntercom(intercomOptions);
        Auth.setLogoutCallback(shutdownIntercom);
      } else if (!isOktaLogin) {
        const intercomOptions = {
          userId: userDetails?.userDetails?.userName,
          email: email[0],
          languageOverride: languagePreference,
          name: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
          customAttributes: {
            cxFirstName: firstName,
            cxLastName: lastName,
            SyscoPortalCA: onboardedFlag,
            PortalCASubscription: esyscoUserType === 1 ? 'Premium' : 'Standard',
            cxLastSeenPortalCA: new Date().toISOString()
          }
        };
        updateIntercom(intercomOptions);
        Auth.setLogoutCallback(shutdownIntercom);
      }
    }
  }, [userDetails, idpUserDetails, updateIntercom, shutdownIntercom]);

  // hide/show intercom based on the route
  useEffect(() => {
    const shouldHideIntercom = HIDE_INTERCOM_PATHS.includes(currentLocation.pathname);

    if (shouldHideIntercom) {
      window.Intercom('shutdown');
    } else {
      window.Intercom('boot', {
        app_id: process.env.REACT_APP_INTERCOM_APP_ID
      });
    }
  }, [currentLocation]);

  // eslint-disable-next-line no-undef
  const remoteImport = async url => (await SystemJS.import(url)).default;

  const { language } = i18n;

  // Helper function to render protected routes with access control
  const renderProtectedRoute = (appRoute, config, props) => {
    if (!hasAppAccess(userDetails, appRoute)) {
      return <AccessDeniedPage />;
    }

    return (
      <ParcelComponent
        config={remoteImport(config)}
        mountParcel={mountRootParcel}
        wrapWith="section"
        language={language}
        className="app-container"
        {...props}
      />
    );
  };

  return (
    <div className="app-container main-container">
      <AppBar
        userDetails={userDetails}
        selectedCustomer={selectedCustomer}
        selectCustomer={setSelectedCustomer}
        isCustomerSelectorVisible={isCustomerSelectorVisible}
        isCustomerFilterVisible={isCustomerFilterVisible}
        changeLanguage={changeLanguage}
        specialOrderCustomers={specialOrderCustomers}
        setSelectedSpecialOrderCustomer={setSelectedSpecialOrderCustomer}
        selectedSpecialOrderCustomer={selectedSpecialOrderCustomer}
      />
      <ErrorBoundary>
        {!isUserLoading ? (
          <Router>
            <Route
              path="/suite/dashboard"
              render={() =>
                renderProtectedRoute(APP_ROUTES.DASHBOARD, '@portal/dashboard', {
                  isCustomerSelectorVisible,
                  setIsCustomerSelectorVisible,
                  isCustomerFilterVisible,
                  setIsCustomerFilterVisible,
                  Auth
                })
              }
            />
            <Route
              path="/suite/delivery"
              render={() =>
                renderProtectedRoute(APP_ROUTES.DELIVERY, '@portal/delivery', {
                  userDetails,
                  isCustomerSelectorVisible,
                  setIsCustomerSelectorVisible,
                  selectCustomer: setSelectedCustomer,
                  selectedCustomer,
                  Auth
                })
              }
            />
            <Route
              path="/suite/specialorders"
              render={() =>
                renderProtectedRoute(APP_ROUTES.SPECIAL_ORDERS, '@portal/specialorders', {
                  userDetails,
                  isCustomerFilterVisible,
                  setIsCustomerFilterVisible,
                  selectedSpecialOrderCustomer,
                  setSelectedSpecialOrderCustomer,
                  setSpecialOrderCustomers,
                  Auth
                })
              }
            />
            <Route
              path="/suite/user-management"
              render={() =>
                renderProtectedRoute(APP_ROUTES.USER_MANAGEMENT, '@portal/user-management', {
                  Auth,
                  userDetails
                })
              }
            />
            <Route path="/login" render={() => <LoginPage changeLanguage={changeLanguage} />} />
          </Router>
        ) : (
          <AppLoader />
        )}
      </ErrorBoundary>
    </div>
  );
}

export default App;
